// Messages flash en toast. Données : ReactShell::Flash, renvoyées à chaque
// visite Inertia.
//
// Chaque visite qui apporte un message le réaffiche (nouvelle clé : animation
// d'entrée rejouée), même s'il est identique au précédent. Fermeture comme
// Bootstrap Alert#close (retrait de .show, fondu, puis retrait), mais par
// React seul : pas de data-bs-dismiss sur le bouton. Le gestionnaire délégué
// de Bootstrap écoute en phase de capture, avant tout stopPropagation de
// React : il retirait du DOM le nœud que React possède, et React plantait en
// le retirant à son tour (page entière démontée, écran noir).
import { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'

export type FlashData = {
  notice: string | null
  alert: string | null
  success: string | null
}

type Kind = keyof FlashData

const BASE_STYLE: CSSProperties = {
  top: '80px',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 1050,
  boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
}
// Sans marge : centré par left 50 % et translateX(-50 %), une marge le
// décalait d'autant (coupé au bord droit sur téléphone).
const SIZE_STYLE: CSSProperties = {
  minWidth: '300px',
  maxWidth: '90vw',
  width: '500px',
  textAlign: 'center',
}
const STYLES: Record<Kind, CSSProperties> = {
  notice: { ...BASE_STYLE, ...SIZE_STYLE },
  alert: { ...BASE_STYLE, ...SIZE_STYLE },
  success: { ...BASE_STYLE, borderLeft: '4px solid #28a745', ...SIZE_STYLE },
}
const AUTO_DISMISS_MS = 5000

export default function Flash({ data }: { data: Record<string, any> }) {
  const flash = data as FlashData
  // Génération de messages : change avec chaque objet `data` reçu du serveur.
  const [seen, setSeen] = useState({ data: flash, generation: 0 })
  let generation = seen.generation
  if (seen.data !== flash) {
    generation += 1
    setSeen({ data: flash, generation })
  }

  return (
    <>
      {flash.notice != null && (
        <FlashAlert key={`notice-${generation}`} kind="notice" message={flash.notice} />
      )}
      {flash.alert != null && (
        <FlashAlert key={`alert-${generation}`} kind="alert" message={flash.alert} />
      )}
      {flash.success != null && (
        <FlashAlert key={`success-${generation}`} kind="success" message={flash.success} />
      )}

      <style>{`
/* Enhanced toast styling */
.alert.position-fixed {
  animation: slideDown 0.3s ease-out;
  font-weight: 600;
  border-radius: 8px;
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  /* La croix (0,75 rem du bord, ~2 rem de large) ne mord pas sur le texte. */
  padding-right: 3.25rem;
}

.alert.position-fixed .btn-close {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  filter: brightness(0) invert(1);
  opacity: 0.8;
  padding: 0.5rem;
}

.alert.position-fixed .btn-close:hover {
  opacity: 1;
}

.alert-success {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  border: none;
  color: #f7f6f4;
}

.alert-danger {
  background: linear-gradient(135deg, #dc3545 0%, #fd7e14 100%);
  border: none;
  color: #f7f6f4;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .alert.position-fixed {
    width: calc(100vw - 40px) !important;
    max-width: none !important;
    min-width: auto !important;
    left: 20px !important;
    transform: none !important;
  }
}
`}</style>
    </>
  )
}

function FlashAlert({ kind, message }: { kind: Kind; message: string }) {
  const [state, setState] = useState<'shown' | 'closing' | 'closed'>('shown')
  const ref = useRef<HTMLDivElement>(null)
  const timer = useRef<number | undefined>(undefined)

  // Bootstrap Alert#close : retire .show, attend la fin du fondu (durée de
  // transition + 5 ms), puis retire l'élément.
  const close = () => {
    if (state !== 'shown' || !ref.current) return
    const cs = getComputedStyle(ref.current)
    const seconds = (v: string) => (parseFloat(v) || 0) * (v.endsWith('ms') ? 0.001 : 1)
    const duration = (seconds(cs.transitionDuration) + seconds(cs.transitionDelay)) * 1000
    setState('closing')
    timer.current = window.setTimeout(() => setState('closed'), duration ? duration + 5 : 0)
  }
  const closeRef = useRef(close)
  closeRef.current = close

  // flash[:success] se ferme seul (data-auto-dismiss), à chaque visite.
  useEffect(() => {
    if (kind !== 'success') return
    const id = window.setTimeout(() => closeRef.current(), AUTO_DISMISS_MS)
    return () => window.clearTimeout(id)
  }, [kind])
  useEffect(() => () => window.clearTimeout(timer.current), [])

  if (state === 'closed') return null

  const color = kind === 'alert' ? 'alert-danger' : 'alert-success'
  const icon = kind === 'alert' ? 'fas fa-exclamation-triangle me-2' : 'fas fa-check-circle me-2'
  return (
    <div
      ref={ref}
      className={`alert ${color} alert-dismissible fade${state === 'shown' ? ' show' : ''} position-fixed`}
      style={STYLES[kind]}
      {...(kind === 'success' ? { 'data-auto-dismiss': String(AUTO_DISMISS_MS) } : {})}
    >
      <i className={icon}></i>{' '}
      {message}{' '}
      <button type="button" className="btn-close" onClick={close}></button>
    </div>
  )
}
